/* New subs — the welcome sequence sent to every new subscriber. One ordered
   message list bound to the account through the assignment; each message waits
   `delay_seconds` after the previous one (the first counts from the subscribe)
   and can carry vault content at one shared price (null/0 = free). */

import type { AutomatedMessage } from '../../../api/client';

export const UNITS = [
  { id: 'sec', secs: 1 },
  { id: 'min', secs: 60 },
  { id: 'h', secs: 3600 },
  { id: 'd', secs: 86400 },
] as const;
export type Unit = (typeof UNITS)[number]['id'];

/** PPV content is free (0 / empty) or priced at least this much — the backend rejects the rest. */
export const MIN_PAID_PRICE = 3;
export const NEW_MESSAGE_WAIT = { value: 30, unit: 'sec' as Unit };

export interface Msg {
  key: string;
  /** null until the message has been created on the server. */
  id: number | null;
  text: string;
  value: number;
  unit: Unit;
  contentIds: string[];
  price: number | null;
}

export interface Stored { id: number; order: number; text: string; delay: number; contentIds: string[]; price: number | null }

let seq = 0;
export const msgKey = () => `m-new-${(seq += 1)}`;

export const secsOf = (m: Pick<Msg, 'value' | 'unit'>) => m.value * UNITS.find((u) => u.id === m.unit)!.secs;

/** Largest whole unit for a raw delay (90 → 90 sec, 7200 → 2 h). */
export function splitDelay(s: number): { value: number; unit: Unit } {
  for (const u of [...UNITS].reverse()) if (s && s % u.secs === 0) return { value: s / u.secs, unit: u.id };
  return { value: s, unit: 'sec' };
}

export function msgOf(m: AutomatedMessage): Msg {
  return { key: `m-${m.message_id}`, id: m.message_id, text: m.message_text, ...splitDelay(m.delay_seconds), contentIds: m.content_ids || [], price: m.content_price ?? null };
}

export function storedOf(m: AutomatedMessage): Stored {
  return { id: m.message_id, order: m.message_order, text: m.message_text, delay: m.delay_seconds, contentIds: m.content_ids || [], price: m.content_price ?? null };
}

export const blankMsg = (): Msg => ({ key: msgKey(), id: null, text: '', ...NEW_MESSAGE_WAIT, contentIds: [], price: null });

/** "1 h 30 min"-style parts for a span of seconds; `fmt` localises one part. */
export function durParts(total: number): { n: number; unit: Unit }[] {
  const out: { n: number; unit: Unit }[] = [];
  let r = total;
  for (const u of [...UNITS].reverse()) {
    const n = Math.floor(r / u.secs);
    if (n) out.push({ n, unit: u.id });
    r -= n * u.secs;
  }
  return out;
}

export const priceInvalid = (p: number | null) => p !== null && p > 0 && p < MIN_PAID_PRICE;

/** The writes that take the server from `saved` to `draft`, in a safe order:
 *  delete, move (via temporary orders, so the unique (list, order) key never
 *  collides), patch the rest, then create the new messages at their final spot. */
export interface SavePlan {
  del: number[];
  tempOrders: { message_id: number; message_order: number }[];
  finalOrders: { message_id: number; message_order: number }[];
  patch: { id: number; body: { message_text?: string; delay_seconds?: number; content_ids?: string[]; content_price?: number | null } }[];
  create: { key: string; body: { message_text: string; message_order: number; delay_seconds: number; content_ids: string[]; content_price: number | null } }[];
}

const TEMP_ORDER_BASE = 100000;

export function planSave(saved: Stored[], draft: Msg[]): SavePlan {
  const kept = new Set(draft.map((m) => m.id).filter((id): id is number => id !== null));
  const byId = new Map(saved.map((s) => [s.id, s]));
  const plan: SavePlan = { del: saved.filter((s) => !kept.has(s.id)).map((s) => s.id), tempOrders: [], finalOrders: [], patch: [], create: [] };
  draft.forEach((m, i) => {
    const order = i + 1;
    const body = { message_text: m.text, delay_seconds: secsOf(m), content_ids: m.contentIds, content_price: m.price };
    const s = m.id !== null ? byId.get(m.id) : undefined;
    if (!s) {
      plan.create.push({ key: m.key, body: { ...body, message_order: order } });
      return;
    }
    if (s.order !== order) {
      plan.tempOrders.push({ message_id: s.id, message_order: TEMP_ORDER_BASE + order });
      plan.finalOrders.push({ message_id: s.id, message_order: order });
    }
    const p: SavePlan['patch'][number]['body'] = {};
    if (s.text !== body.message_text) p.message_text = body.message_text;
    if (s.delay !== body.delay_seconds) p.delay_seconds = body.delay_seconds;
    if (s.contentIds.join() !== body.content_ids.join()) p.content_ids = body.content_ids;
    if ((s.price ?? null) !== (body.content_price ?? null)) p.content_price = body.content_price;
    if (Object.keys(p).length) plan.patch.push({ id: s.id, body: p });
  });
  return plan;
}
